/**
 * Hook for tracking scroll depth and time on page
 * Sends analytics events at 25%, 50%, 75%, and 100% scroll depth
 * Tracks time on page after 30 seconds
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackScrollDepth, trackTimeOnPage } from '@/utils/analytics';

export const useScrollTracking = () => {
  const location = useLocation();
  const scrollDepthsTracked = useRef<Set<number>>(new Set());
  const startTime = useRef<number>(Date.now());
  const timeTracked = useRef<boolean>(false);

  useEffect(() => {
    // Reset tracking on route change
    scrollDepthsTracked.current.clear();
    startTime.current = Date.now();
    timeTracked.current = false;

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      
      // Calculate scroll percentage
      const scrollPercentage = Math.round(
        ((scrollTop + windowHeight) / documentHeight) * 100
      );

      // Track at 25%, 50%, 75%, and 100%
      const milestones = [25, 50, 75, 100];
      
      milestones.forEach((milestone) => {
        if (
          scrollPercentage >= milestone &&
          !scrollDepthsTracked.current.has(milestone)
        ) {
          scrollDepthsTracked.current.add(milestone);
          trackScrollDepth(milestone, location.pathname);
        }
      });
    };

    // Track time on page after 30 seconds
    const timeTrackingInterval = setInterval(() => {
      if (!timeTracked.current) {
        const timeSpent = Math.floor((Date.now() - startTime.current) / 1000);
        if (timeSpent >= 30) {
          trackTimeOnPage(timeSpent, location.pathname);
          timeTracked.current = true;
        }
      }
    }, 5000); // Check every 5 seconds

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(timeTrackingInterval);
    };
  }, [location.pathname]);
};


